import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import posthog from "posthog-js";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { displayToast } from "@/utils/toast";
import { ToastType } from "@/enums";
import { User } from "@/types/user";

export interface VerifyOAuthEmailRequest {
  code: string;
  pending_authentication_token: string;
  provider: string;
}

export interface VerifyOAuthEmailResponse {
  success: boolean;
  access_token: string;
  user: User;
  is_new_user: boolean;
  message: string;
}

export const useVerifyOAuthEmail = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  return useMutation(
    ["verify-oauth-email"],
    async (data: VerifyOAuthEmailRequest): Promise<VerifyOAuthEmailResponse> =>
      fetch(ENDPOINTS.auth.verify_oauth_email, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code: data.code,
          pending_authentication_token: data.pending_authentication_token,
          provider: data.provider,
        }),
      }).then(handleResponse),
    {
      onSuccess: ({ access_token, user, is_new_user, message }) => {
        displayToast(message || "Email verified successfully", ToastType.success);
        auth?.login(access_token, user);

        if (is_new_user) {
          posthog.capture("Signed Up");
        }

        if (!moment(user.created_at).isBefore(moment().toISOString())) {
          navigate("/onboarding");
        } else {
          navigate("/");
        }
      },
    },
  );
};
